import throttle from "lodash/throttle";
import { RefObject, useRef } from "react";
import useIsomorphicLayoutEffect from "web/react/hooks/use-isomorphic-layout-effect/use-isomorphic-layout-effect";

/**
 * Attach the returned ref to a <tbody> to make every <tr> inside it match the tallest row's
 * natural height, re-measuring on resize. Pass `enabled: false` to reset back to auto.
 */
export function useEqualRowHeight(enabled: boolean): RefObject<HTMLTableSectionElement> {
    const tbodyRef = useRef<HTMLTableSectionElement>(null);

    useIsomorphicLayoutEffect(() => {
        const tbody = tbodyRef.current;
        if (!tbody) {
            return;
        }

        function equalize(tbodyElement: HTMLTableSectionElement): void {
            const rowElements = Array.from(tbodyElement.querySelectorAll("tr"));
            rowElements.forEach((row) => {
                row.style.height = "";
            });

            if (!enabled || rowElements.length === 0) {
                return;
            }

            const tallest = Math.max(
                ...rowElements.map((row) => row.getBoundingClientRect().height)
            );
            rowElements.forEach((row) => {
                row.style.height = `${tallest}px`;
            });
        }

        equalize(tbody);

        const handleResize = throttle(() => equalize(tbody), 200);
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
            handleResize.cancel();
        };
    }, [enabled]);

    return tbodyRef;
}
